import * as React from 'react';
// Import Libraries
import styled from 'styled-components';
import axios from 'axios';
import moment, { Moment } from 'moment';
import { v4 as uuidv4 } from 'uuid';
import Swal from 'sweetalert2';
import errorReporter from '@app/errorReporter';
import { Alert } from '@brokerbay/design-components';
import {
  TemplateUsage as TemplateType,
  TemplateRoles,
  TEMPLATE_ROLE_PRIORITY_MAP,
} from '@bb/domain/dist/types/Template';
import {
  replaceMarkupWithTags,
  replaceTagsWithMarkup,
  defaultTemplates,
  sortTemplates,
  isLimitedAccessOnUnclaimedBrokerage,
  isLimitedAccessOnNonActiveBrokerage,
} from 'utils';
import { FroalaTaggedInput, message as antMessage } from 'components';
import type { RootScopeType } from '@app/caferealtor/Default.controller';

import { Template } from 'api/TemplateController/getTemplates';
import type { TemplateListAllResponse } from '@/app-package';
import PanelHeader from '../../react-common/PanelHeader';
import PanelBody from '../../react-common/PanelBody';
import Loading from '../../react-common/Loading';
import Toggle from '../../react-common/Toggle';
import { HistoryModalButton } from '../../history/HistoryModal/HistoryModal';
import { DatePicker, TimePicker } from '../../react-common/Pickers';

import TemplateHeader from '../../../Router/pages/TemplateManager/TemplateHeader';
import NotificationGroup from './NotificationGroup';
import { TemplateDropdownType } from '../../template/TemplateDropdown';

import NoAccessAlert from '../../listing/alerts/NoAccessAlert';

// Localizer for DateTimePicker
moment.locale('en');

export const defaultMessagePrefix =
  'Please be advised that an offer has been registered on';
export const defaultMessageTotalOffers = `TOTAL # OFFERS:`;

// eslint-disable-next-line @typescript-eslint/no-namespace
declare namespace OfferSettings {
  type SubmissionType = 'inPerson' | 'email' | 'other';
  interface Submission {
    active?: boolean;
    details?: string;
  }
  interface Registration {
    shouldSubmitOfferDoc?: boolean;
    shouldAllowMls?: boolean;
    shouldVerify801?: boolean;
    isEnabled?: boolean;
  }
  interface Data {
    id?: string;
    listing: string;
    acceptedAnytime: boolean;
    agentGroup: string;
    subject?: string;
    submission: Record<SubmissionType, Submission>;
    registration: Registration;
    offerDate: Date;
    shouldShowOfferCount: boolean;
    message: string;
    templateId: string;
    preemptive: boolean;
    requestIrrevocable: number;
    otherInstructions: string;
  }
}

// TODO: type is incomplete
type OfferSettingsProps = any & { $rootScope: RootScopeType };

interface OfferSettingsState {
  datePickerValue?: string;
  isInitializing: boolean;
  isLoading?: boolean;
  showHistoryModal: boolean;
  isTemplateLoading: boolean;
  templates: TemplateDropdownType;
  previewing: boolean;
  selectedTemplate: string;
  offerSettings?: OfferSettings.Data;
  defaultMessage?: string;
  timePickerValue?: string;
  listing?: { id: string; line1: string; line2: string };
  errors: { pastDate?: boolean; requestIrrevocable?: string };
}

// TODO: Missing Functionality - Eyeball Button; History Modal Button
// TODO: Refactor into more components
class OfferSettings extends React.Component<
  OfferSettingsProps,
  OfferSettingsState
> {
  constructor(props: OfferSettingsProps) {
    super(props);
    this.state = {
      isInitializing: true,
      showHistoryModal: false,
      isTemplateLoading: true,
      templates: [],
      previewing: false,
      selectedTemplate: '',
      errors: {},
    };
  }

  /**
   * Wrapper around `this.setState({ offerSettings })` to provide async
   * setState support.
   * @param partial Partial `OfferSettingsData` or a value provider function.
   */
  private setOfferSettings(
    partial:
      | Partial<OfferSettings.Data>
      | ((value: OfferSettings.Data) => Partial<OfferSettings.Data>),
  ) {
    return new Promise<void>(resolve => {
      this.setState(
        ({ offerSettings }) => ({
          offerSettings: {
            ...offerSettings,
            ...(typeof partial === 'function'
              ? partial(offerSettings)
              : partial),
          },
        }),
        resolve,
      );
    });
  }

  redirectToOffers = () => {
    const {
      $routeParams: { listingId },
    } = this.props;
    window.location.replace(`#/listing/${listingId}/offer`);
  };

  saveOfferSettings = async (redirect: boolean) => {
    const {
      $routeParams: { listingId },
    } = this.props;
    const { offerSettings } = this.state;
    this.setState({ isLoading: true, isInitializing: redirect });

    // Convert markup back to `{{...}}` tag format
    const taggedOfferSettings = {
      ...offerSettings,
      message: replaceMarkupWithTags(offerSettings.message),
      subject: offerSettings.subject || '',
    };

    try {
      await axios.post(
        `/listing/${listingId}/offer_settings`,
        taggedOfferSettings,
      );
      if (redirect) this.redirectToOffers();
    } catch (error) {
      Swal.fire({
        title: 'Error update offer settings',
        text:
          'Something happened and we could not process the offer settings ' +
          'update. Please try again.',
        type: 'error',
      });
      errorReporter.error(`Error saving offer settings: ${error}`);
    }
    this.setState({ isLoading: false, isInitializing: false });
  };

  getOfferSettings = async () => {
    const {
      $routeParams: { listingId },
    } = this.props;

    const [{ data: listing }, { data: offerSettings }] = await Promise.all([
      axios.get(`/listing/${listingId}`),
      axios.get(`/listing/${listingId}/offer_settings`),
    ]);
    offerSettings.preemptive = !!offerSettings.preemptive;
    offerSettings.registration = offerSettings.registration || {
      shouldAllowMls: true,
      shouldSubmitOfferDoc: true,
      shouldVerify801: true,
    };
    offerSettings.message = replaceTagsWithMarkup(offerSettings.message);
    offerSettings.subject = `NEW REGISTERED OFFER (# TOTAL) - ${
      listing.line1
    } ${listing.line2 || ''}`;
    this.setState({
      offerSettings,
      defaultMessage: offerSettings.message,
      listing,
      isInitializing: false,
      errors: {
        pastDate:
          !offerSettings.acceptedAnytime &&
          new Date(offerSettings.offerDate) < new Date(),
      },
    });
  };

  getTemplates = async () => {
    const {
      $routeParams: { listingId },
    } = this.props;
    const { offerSettings, defaultMessage } = this.state;

    const [
      { data },
      {
        data: { signature },
      },
    ] = await Promise.all([
      axios.get<Template[]>(`/listing/${listingId}/template/list?type=offer`),
      axios.get<{ signature: string }>(
        `/listing/${listingId}/template/rep_signature`,
      ),
    ]);

    let templates: TemplateDropdownType = data.map(template => ({
      ...template,
      body: template.body.concat(signature),
    }));

    // Add a root default template if necessary
    if (!templates.some(t => t.isDefault)) {
      templates.push({
        ...defaultTemplates.offer,
        body: defaultTemplates.offer.body.concat(signature),
        id: 'default',
        name: 'Default Offer Registration Template',
        type: 'offer',
        isDefault: true,
        role: 'GLOBAL',
        updatedAt: undefined,
      });
    }
    const primaryDefault = templates
      .filter(t => t.isDefault)
      .sort(
        (a, b) =>
          TEMPLATE_ROLE_PRIORITY_MAP[a.role] -
          TEMPLATE_ROLE_PRIORITY_MAP[b.role],
      )[0];

    templates = sortTemplates(templates);
    // Add a custom template selection
    const custom = {
      body:
        offerSettings?.id && defaultMessage
          ? replaceMarkupWithTags(defaultMessage)
          : signature,
      id: 'custom',
      name: 'Custom',
      type: 'offer' as TemplateType,
      role: 'USER' as TemplateRoles,
    };
    templates.push(custom);

    this.setState({
      isTemplateLoading: false,
      templates,
      selectedTemplate: offerSettings?.templateId || primaryDefault.id,
    });
    this.setOfferSettings({
      message: replaceTagsWithMarkup(
        offerSettings?.templateId ? defaultMessage : primaryDefault.body,
      ),
    });
  };

  onOffersAcceptedChange = (event: React.FormEvent<HTMLSelectElement>) => {
    event.preventDefault();
    const acceptedAnytime = event.currentTarget.value === 'true';
    this.setOfferSettings({ acceptedAnytime });
    if (!acceptedAnytime) {
      this.setOfferSettings({ preemptive: true });
    }
    this.setState(prev => ({
      errors: {
        pastDate:
          prev.offerSettings?.offerDate && !acceptedAnytime
            ? new Date(prev.offerSettings.offerDate) < new Date()
            : false,
      },
    }));
  };

  onDateChange = (date: Moment, value: string) => {
    const newDateTime = moment(date).seconds(0).toDate();
    this.setState({
      datePickerValue: value,
      errors: { pastDate: newDateTime < new Date() },
    });
    this.setOfferSettings({ offerDate: newDateTime });
  };

  onTimeChange = (time: Moment, value: string) => {
    const { offerSettings } = this.state;
    const { offerDate = new Date() } = offerSettings;
    const hour = moment(time).hours();
    const min = moment(time).minutes();
    const newDateTime = moment(offerDate)
      .hours(hour)
      .minutes(min)
      .seconds(0)
      .toDate();
    this.setState({
      timePickerValue: value,
      errors: { pastDate: newDateTime < new Date() },
    });
    this.setOfferSettings({ offerDate: newDateTime });
  };

  onPreemptiveToggle = (event: React.MouseEvent) => {
    event.preventDefault();
    this.setOfferSettings(({ preemptive }) => ({ preemptive: !preemptive }));
  };

  onIrrevocableChange = (event: React.FormEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    if (value === '') {
      this.setOfferSettings({ requestIrrevocable: null });
      this.setState({
        errors: { requestIrrevocable: undefined },
      });
      return;
    }
    const numberValue = Number(value);
    if (Number.isNaN(numberValue) || numberValue < 0) {
      this.setState({
        errors: {
          requestIrrevocable:
            'Requested Min Irrevocable must be a non-negative number.',
        },
      });
      return;
    }
    if (!Number.isInteger(numberValue)) {
      this.setState({
        errors: {
          requestIrrevocable: 'Requested Min Irrevocable must be an integer.',
        },
      });
      return;
    }
    if (numberValue > 100) {
      this.setState({
        errors: {
          requestIrrevocable:
            'Requested Min Irrevocable must be less than 100 hours.',
        },
      });
      return;
    }
    this.setOfferSettings({ requestIrrevocable: numberValue });
    this.setState({
      errors: { requestIrrevocable: undefined },
    });
  };

  onInstructionChange = (event: React.FormEvent<HTMLTextAreaElement>) => {
    this.setOfferSettings({ otherInstructions: event.currentTarget.value });
  };

  onNotificationChange = (event: React.FormEvent<HTMLInputElement>) => {
    this.setOfferSettings({ agentGroup: event.currentTarget.value });
  };

  onSubmissionChange = ({
    currentTarget: node,
  }: React.FormEvent<HTMLInputElement>) => {
    const key = node.value as OfferSettings.SubmissionType;
    const active = node.checked;
    this.setOfferSettings(({ submission }) => ({
      submission: { ...submission, [key]: { ...submission[key], active } },
    }));
  };

  onSubmissionDetailsChangeGen =
    (type: OfferSettings.SubmissionType) =>
    (event: React.FormEvent<HTMLInputElement>) => {
      const { value } = event.currentTarget;
      this.setOfferSettings(({ submission }) => ({
        submission: {
          ...submission,
          [type]: { ...submission[type], details: value },
        },
      }));
    };

  onRegistrationToggleGen =
    (type: keyof OfferSettings.Registration) =>
    async (event: React.MouseEvent) => {
      event.preventDefault();
      await this.setOfferSettings(({ registration }) => {
        const newRegistration: OfferSettings.Registration = {
          ...registration,
          [type]: !registration[type],
        };

        // When shouldSubmitOfferDoc toggles, shouldSubmitVerify801 follows its state
        if (type === 'shouldSubmitOfferDoc') {
          newRegistration.shouldVerify801 = newRegistration[type];
        }

        return { registration: newRegistration };
      });

      if (type === 'isEnabled') {
        await this.saveOfferSettings(false);
      }
    };

  onFroalaChange = (message: string) => {
    this.setOfferSettings({ message });
  };

  onSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.target.value;
    this.setOfferSettings({ subject: newVal });
  };

  openPreviewModal = () => {
    const { $rootScope, ModalService } = this.props;
    const { listing } = this.state;
    $rootScope.$apply(() => {
      ModalService.openPreviewModal(listing);
    });
  };

  onHistoryButtonClick = () => {
    this.setState({ showHistoryModal: true });
  };

  onModalHide = () => {
    this.setState({ showHistoryModal: false });
  };

  onFroalaPreview = (previewing: boolean) => {
    this.setState({ previewing });
  };

  onNotificationOfferToggle = () => {
    this.setOfferSettings(({ shouldShowOfferCount }) => ({
      shouldShowOfferCount: !shouldShowOfferCount,
    }));
  };

  onClickDisabledInput = () => {
    antMessage.warning({
      content: (
        <span>
          Click <strong>Exit Preview</strong> to make changes.
        </span>
      ),
    });
  };

  async componentDidMount() {
    const {
      $routeParams: { listingId },
    } = this.props;

    try {
      await this.getOfferSettings();
      await this.getTemplates();
    } catch (err) {
      errorReporter.error(err);
      console.error(err);
      window.location.replace(`#/listing/${listingId}/view`);
      antMessage.error({
        content:
          'You do not have permissions to view this page. Please contact support@brokerbay.com for more information.',
      });
    }
  }

  useTemplate = (id: string) => {
    const { templates } = this.state;
    const templateBody = templates.find(t => t.id === id).body;
    this.setState({ selectedTemplate: id });
    this.setOfferSettings({
      message: replaceTagsWithMarkup(templateBody),
      templateId: id,
    });
  };

  render() {
    const {
      listing,
      isLoading,
      isInitializing,
      offerSettings = {} as OfferSettings.Data,
      showHistoryModal,
      timePickerValue,
      datePickerValue,
      isTemplateLoading,
      templates,
      selectedTemplate,
      previewing,
      errors,
    } = this.state;
    const { $rootScope } = this.props;
    const { isMobileView } = $rootScope;
    const isLimitedAccessUnclaimed = isLimitedAccessOnUnclaimedBrokerage(
      $rootScope.auth,
      $rootScope.orgStatus,
      $rootScope.currentPlan,
    );
    const isLimitedAccessNonActive = isLimitedAccessOnNonActiveBrokerage(
      $rootScope.auth,
      $rootScope.orgStatus,
      $rootScope.currentPlan,
    );

    const panelBodyStyles: React.CSSProperties = {
      // blurRadius is same as theme.pageBlurRadius;
      // Both filter & backdropFilter is required in order to make it blur;
      filter: isLimitedAccessUnclaimed ? 'blur(1.5px)' : undefined,
      backdropFilter: isLimitedAccessUnclaimed ? 'blur(1.5px)' : undefined,
      pointerEvents: isLimitedAccessUnclaimed ? 'none' : undefined,
    };

    const buttonStyles = { margin: isMobileView ? '0.5em 0em' : '0em 0.5em' };

    const inputStyles = {
      margin: isMobileView && '0.5em 0em',
      width: isMobileView ? '100%' : '15%',
    };
    const {
      submission = {} as OfferSettings.Data['submission'],
      registration = {},
      shouldShowOfferCount = true,
    } = offerSettings;

    const eyeballButton = (
      <button
        key={uuidv4()}
        className="btn btn-default"
        style={buttonStyles}
        onClick={this.openPreviewModal}
        disabled={isLimitedAccessNonActive}
      >
        <i className="pe-7s-look fat"></i>
      </button>
    );

    const saveButton = (
      <button
        key={uuidv4()}
        className="btn btn-primary"
        style={buttonStyles}
        onClick={() => this.saveOfferSettings(true)}
        disabled={
          isLimitedAccessUnclaimed || Object.values(errors).some(val => val)
        }
        data-cy="save-offer-settings"
      >
        Save
      </button>
    );
    const cancelButton = (
      <button
        key={uuidv4()}
        className="btn btn-default"
        onClick={this.redirectToOffers}
        disabled={isLimitedAccessUnclaimed}
      >
        Cancel
      </button>
    );
    const headerTitle = listing && (
      <>
        <a className="listing-link" href={`#/listing/${listing.id}/view`}>{`${
          listing.line1
        } ${listing.line2 || ''}`}</a>
        <span> - Offer Settings </span>
      </>
    );
    return isInitializing ? (
      <Loading size="fa-5x" />
    ) : (
      <>
        <PanelHeader
          overAlert={isLimitedAccessUnclaimed && <NoAccessAlert />}
          title={headerTitle}
          extra={[
            <div
              key={uuidv4()}
              style={{
                display: 'flex',
                marginLeft: isMobileView && '10px',
                flexDirection: isMobileView ? 'column' : 'row',
                alignItems: isMobileView && 'baseline',
              }}
            >
              <AllowRegistrationToggleContainer $isMobileView={isMobileView}>
                <strong style={{ display: 'inline' }}>
                  Allow Offer Registrations:{' '}
                </strong>
                <Toggle
                  model={registration.isEnabled}
                  onClick={this.onRegistrationToggleGen('isEnabled')}
                  disabled={isLimitedAccessUnclaimed}
                />
              </AllowRegistrationToggleContainer>
              {eyeballButton}
              <HistoryModalButton
                onHide={this.onModalHide}
                key={uuidv4()}
                onClick={this.onHistoryButtonClick}
                show={showHistoryModal}
                id={offerSettings.id}
                type="OfferSettings"
                disabled={isLimitedAccessUnclaimed}
              />
              {saveButton}
              {cancelButton}
            </div>,
          ]}
        />
        <PanelBody
          disabled={!registration.isEnabled}
          loading={isLoading}
          style={panelBodyStyles}
        >
          <div className="offer-settings">
            <div className="offer-settings-section">
              <legend>Offer Instructions</legend>
              <div className="offer-settings-row">
                <div
                  style={{
                    width: 'max(20%, 140px)',
                    marginRight: 'min(1em, 2vw)',
                  }}
                >
                  <label>Offers Accepted:</label>
                  <select
                    value={offerSettings.acceptedAnytime.toString()}
                    onChange={this.onOffersAcceptedChange}
                    className="form-control"
                    data-cy="offer-accept-time"
                  >
                    <option value="true">Anytime</option>
                    <option value="false">On Offer Date</option>
                  </select>
                </div>
                {!offerSettings.acceptedAnytime && (
                  <>
                    <div style={{ width: 'max(20%, 175px)' }}>
                      <label>Date:</label>
                      <div className="date-time-container">
                        <div>
                          <DatePicker
                            value={offerSettings.offerDate}
                            inputValue={datePickerValue}
                            onChange={this.onDateChange}
                            minDate={new Date()}
                            format="YYYY-MM-DD"
                            variant="inline"
                            inputVariant="outlined"
                            minDateMessage="Offer date should not be earlier than the current date"
                            autoOk
                            disablePast
                          />
                        </div>
                      </div>
                    </div>
                    <div style={{ width: 'max(20%, 175px)' }}>
                      <label>Time:</label>
                      <div className="date-time-container">
                        <div>
                          <TimePicker
                            value={offerSettings.offerDate}
                            inputValue={timePickerValue}
                            onChange={this.onTimeChange}
                            placeholder="11:59 PM"
                            mask="__:__ _M"
                            variant="inline"
                            inputVariant="outlined"
                            autoOk
                          />
                        </div>
                      </div>
                      {errors.pastDate && (
                        <label className="error">
                          Irrevocable date must be in the future.
                        </label>
                      )}
                    </div>
                    <div style={{ width: 'max(20%, 140px)' }}>
                      <label>Pre-emptive Offers Accepted:</label>
                      <div className="from-control">
                        <Toggle
                          onClick={this.onPreemptiveToggle}
                          model={offerSettings.preemptive}
                          dataCy="offer-toggle-preemptive"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="offer-settings-row">
                <div style={{ flex: '0 0 48%' }}>
                  <label>Requested Min Irrevocable (Hours):</label>
                  <input
                    style={{ maxWidth: isMobileView ? '100%' : '15%' }}
                    value={offerSettings.requestIrrevocable}
                    onChange={this.onIrrevocableChange}
                    type="number"
                    className="form-control"
                    data-cy="offer-irrevocable-time"
                    min={0}
                    step={1}
                  ></input>
                  {errors.requestIrrevocable && (
                    <label className="error">{errors.requestIrrevocable}</label>
                  )}
                </div>
                <div style={{ flex: '0 0 48%' }}>
                  <label>Other Instructions for Showing Agents:</label>
                  <textarea
                    placeholder="Other Instructions"
                    value={offerSettings.otherInstructions}
                    onChange={this.onInstructionChange}
                    rows={3}
                    className="form-control"
                    data-cy="offer-other-instructions"
                  ></textarea>
                </div>
              </div>
            </div>

            <div className="offer-settings-half-row">
              <legend>Offer Submission Methods</legend>
              <ul className="offer-submission-methods">
                <li>
                  <input
                    type="checkbox"
                    onChange={this.onSubmissionChange}
                    checked={submission?.inPerson?.active || false}
                    value="inPerson"
                    data-cy="offer-submission-in-person"
                  ></input>
                  <label style={{ paddingLeft: '3em' }}>In Person</label>
                  <input
                    className="submission-details form-control pull-right"
                    placeholder="Location"
                    style={inputStyles}
                    onChange={this.onSubmissionDetailsChangeGen('inPerson')}
                    value={submission?.inPerson?.details || ''}
                    disabled={!submission?.inPerson?.active}
                    type="text"
                    data-cy="offer-submission-location"
                  ></input>
                </li>
                <li>
                  <input
                    type="checkbox"
                    onChange={this.onSubmissionChange}
                    checked={submission?.email?.active || false}
                    value="email"
                    data-cy="offer-submission-email"
                  ></input>
                  <label style={{ paddingLeft: '3em' }}>Email</label>
                  <input
                    className="submission-details form-control pull-right"
                    placeholder="Email Address"
                    style={inputStyles}
                    onChange={this.onSubmissionDetailsChangeGen('email')}
                    value={submission?.email?.details || ''}
                    disabled={!submission?.email?.active}
                    type="text"
                    data-cy="offer-submission-email-address"
                  ></input>
                </li>
                <li>
                  <input
                    type="checkbox"
                    onChange={this.onSubmissionChange}
                    checked={submission?.other?.active || false}
                    value="other"
                    data-cy="offer-submission-other"
                  ></input>
                  <label style={{ paddingLeft: '3em' }}>Other</label>
                  <input
                    className="submission-details form-control pull-right"
                    placeholder="Special Instructions"
                    style={inputStyles}
                    onChange={this.onSubmissionDetailsChangeGen('other')}
                    value={submission?.other?.details || ''}
                    disabled={!submission?.other?.active}
                    type="text"
                    data-cy="offer-submission-other-instructions"
                  ></input>
                </li>
              </ul>
            </div>

            <div className="offer-settings-half-row">
              <legend>Online Registration Settings</legend>
              <ul style={{ listStyle: 'none', padding: 0, width: '100%' }}>
                <li style={{ width: '100%' }}>
                  <label className="control-label">
                    Allow showing agents to register their own offers:
                  </label>
                  <RegistrationSettingToggleContainer>
                    <Toggle
                      onClick={this.onRegistrationToggleGen('shouldAllowMls')}
                      model={registration.shouldAllowMls}
                      dataCy="offer-allow-mls"
                    />
                  </RegistrationSettingToggleContainer>
                </li>
                <li style={{ width: '100%' }}>
                  <label className="control-label">
                    Make Offer Doc attachment mandatory when registering online:
                  </label>
                  <RegistrationSettingToggleContainer>
                    <Toggle
                      onClick={this.onRegistrationToggleGen(
                        'shouldSubmitOfferDoc',
                      )}
                      model={registration.shouldSubmitOfferDoc}
                      dataCy="offer-submit-doc"
                    />
                  </RegistrationSettingToggleContainer>
                </li>
                {registration.shouldSubmitOfferDoc && (
                  <li>
                    <label
                      style={{ display: 'block' }}
                      className="control-label"
                    >
                      Offer Docs submitted online MUST be verified{' '}
                      <small>(before notifications are sent)</small>:
                    </label>
                    <RegistrationSettingToggleContainer>
                      <Toggle
                        onClick={this.onRegistrationToggleGen(
                          'shouldVerify801',
                        )}
                        model={registration.shouldVerify801}
                        dataCy="offer-verify-801"
                      />
                    </RegistrationSettingToggleContainer>
                  </li>
                )}
              </ul>
            </div>

            <div className="offer-settings-offer-notification">
              <legend>Offer Registration Notification</legend>
              <div>
                <div>
                  <AlertContainer>
                    <Alert
                      type="info"
                      showIcon
                      message="The message template below will be delivered to the selected
                    group based on your preferences when an offer is registered."
                    />
                  </AlertContainer>
                  <TemplateHeader
                    templates={templates}
                    selectedTemplate={selectedTemplate}
                    dropdownDisabled={isTemplateLoading}
                    subject={offerSettings.subject}
                    previewing={previewing}
                    disableSubject
                    onPreview={this.onFroalaPreview}
                    onDropdownChange={this.useTemplate}
                    onInputChange={this.onSubjectChange}
                    limitWidth
                  />
                  <FroalaTaggedInput
                    model={selectedTemplate ? offerSettings.message : ''}
                    onModelChange={this.onFroalaChange}
                    templateType="offer"
                    preview={previewing}
                    onClick={() => {
                      if (previewing) this.onClickDisabledInput();
                    }}
                    disabled={selectedTemplate !== 'custom'}
                  />
                </div>
                <div>
                  <NotificationGroup
                    checked={offerSettings.agentGroup}
                    onNotificationChange={this.onNotificationChange}
                  />
                  <div className="offer-settings-offer-notification-include-number">
                    <label>
                      Include # of registered offers in showing confirmations:
                    </label>
                    <Toggle
                      onClick={this.onNotificationOfferToggle}
                      model={shouldShowOfferCount}
                      dataCy="include-offer-numbers"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </PanelBody>
      </>
    );
  }
}

export default OfferSettings;

const AlertContainer = styled.div`
  margin-bottom: 16px;
`;

const RegistrationSettingToggleContainer = styled.div`
  height: 34px;
  display: flex;
  align-items: center;
`;

const AllowRegistrationToggleContainer = styled.span<{
  $isMobileView: boolean;
}>`
  display: flex;
  flex-direction: ${({ $isMobileView }) => ($isMobileView ? 'column' : 'row')};
  justify-content: space-between;
  align-items: ${({ $isMobileView }) =>
    $isMobileView ? 'flex-start' : 'center'};
  width: 18em;
  margin-right: 20em;
`;
